import React, { useState, useEffect, useRef } from 'react';
import PastPoetryQuestions from '../past_questions/PastPoetryQuestions';
import PastCompositionQuestions from '../past_questions/PastCompositionQuestions';
import PastComparativeQuestions from '../past_questions/PastComparativeQuestions';
import PastShakespeareQuestions from '../past_questions/PastShakespeareQuestions';
import PastGCSEShakespeareQuestions from '../past_questions/PastGCSEShakespeareQuestions';
import PastGCSE19thQuestions from '../past_questions/PastGCSE19thQuestions';
import PastGCSEInspectorQuestions from '../past_questions/PastGCSEInspectorQuestions';
import PastGCSEPoetryQuestions from '../past_questions/PastGCSEPoetryQuestions';
import PastAisteQuestions from '../past_questions/PastAisteQuestions';
import PastFiliochtQuestions from '../past_questions/PastFiliochtQuestions';
import PastFrenchQuestions from '../past_questions/PastFrenchQuestions';
import PastSpanishQuestions from '../past_questions/PastSpanishQuestions';
import PastGermanQuestions from '../past_questions/PastGermanQuestions';
import PastIrishOralQuestions from '../past_questions/PastIrishOralQuestions';
import PastGCSESpanishQuestions from '../past_questions/PastGCSESpanishQuestions';
import PastGCSEFrenchQuestions from '../past_questions/PastGCSEFrenchQuestions';
import PastGCSEGermanQuestions from '../past_questions/PastGCSEGermanQuestions';
import ComprehensionUpload from './ComprehensionUpload';
import ComprehensionView from './ComprehensionView';
import '../styles/QuestionSelect.scss';

function QuestionSelect({ 
  category, 
  question, 
  setQuestion, 
  handlePrevStep, 
  handleQuestionSubmit,
  selectedPoem,
  API_BASE_URL,
  authToken,
  userData
}) {
  const [selectedPoet, setSelectedPoet] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedComparativeType, setSelectedComparativeType] = useState("");
  const [selectedPlay, setSelectedPlay] = useState("");
  const textareaRef = useRef(null);
  
  // Auto-resize the textarea when content changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(400, Math.max(120, textareaRef.current.scrollHeight))}px`;
    }
  }, [question]);

  // Handle Comprehension category
  if (category === 'Comprehension') {
    return userData?.role === 'teacher' ? (
      <ComprehensionUpload 
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        userData={userData}
      />
    ) : (
      <ComprehensionView
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        userData={userData}
        onSubmit={(result) => {
          setQuestion(result.title);
          handleQuestionSubmit();
        }}
      />
    );
  }

  return (
    <div data-step="2">
      <h2>{category.startsWith('Filiocht_') ? 
        `Filíocht: Cuid ${category.split('_')[1]}` : 
        category === 'GCSE_Shakespeare' ? 'Shakespeare' : 
        category === 'GCSE_19th' ? '19th Century Novel' : 
        category === 'GCSE_Inspector' ? 'An Inspector Calls' : 
        category === 'GCSE_Poetry' ? 'Poetry' : 
        category === 'FR_Oral' ? 'Oral Exam (French)' :
        category === 'ES_Oral' ? 'Oral Exam (Spanish)' :
        category === 'DE_Oral' ? 'Oral Exam (German)' :
        category === 'IE_Oral' ? 'Oral Exam (Irish)' :
        category === 'GCSE_French_Q1' ? 'GCSE French Question 1' :
        category === 'GCSE_French_Q2' ? 'GCSE French Question 2' :
        category === 'GCSE_Spanish_Q1' ? 'GCSE Spanish Question 1' :
        category === 'GCSE_Spanish_Q2' ? 'GCSE Spanish Question 2' :
        category === 'GCSE_German_Q1' ? 'GCSE German Question 1' :
        category === 'GCSE_German_Q2' ? 'GCSE German Question 2' :
        category.startsWith('FR_') ? `${category.substring(3)} (French)` :
        category.startsWith('ES_') ? `${category.substring(3)} (Spanish)` :
        category.startsWith('DE_') ? `${category.substring(3)} (German)` :
        category}
      </h2>
      <h3>Enter Your Question or Choose a Past Exam Question</h3>
      <h4>If you don't see your question in the dropdown, just enter it in the text box below.</h4>
      
      {/* Category-specific dropdowns first */}
      {category === "Shakespeare" && (
        <div className="select-container">
          <label>
            Choose a play:
            <select
              className="common-select"
              value={selectedPlay}
              onChange={(e) => {
                setSelectedPlay(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a play --</option>
              {Object.keys(PastShakespeareQuestions).map((playName) => (
                <option key={playName} value={playName}>
                  {playName}
                </option>
              ))}
            </select>
          </label>

          {selectedPlay && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastShakespeareQuestions[selectedPlay].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {category === "GCSE_Shakespeare" && (
        <div className="select-container">
          <label>
            Choose a play:
            <select
              className="common-select"
              value={selectedPlay}
              onChange={(e) => {
                setSelectedPlay(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a play --</option>
              {Object.keys(PastGCSEShakespeareQuestions).map((playName) => (
                <option key={playName} value={playName}>
                  {playName}
                </option>
              ))}
            </select>
          </label>

          {selectedPlay && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastGCSEShakespeareQuestions[selectedPlay].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {category === "Poetry" && (
        <div className="select-container">
          <label>
            Choose a poet:
            <select
              className="common-select"
              value={selectedPoet}
              onChange={(e) => {
                setSelectedPoet(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a poet --</option>
              {Object.keys(PastPoetryQuestions).map((poetName) => (
                <option key={poetName} value={poetName}>
                  {poetName}
                </option>
              ))}
            </select>
          </label>

          {selectedPoet && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastPoetryQuestions[selectedPoet].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {category === "Composition" && (
        <div className="select-container">
          <label>
            Choose a type of question:
            <select
              className="common-select"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a type of question --</option>
              {Object.keys(PastCompositionQuestions).map((typeName) => (
                <option key={typeName} value={typeName}>
                  {typeName}
                </option>
              ))}
            </select>
          </label>

          {selectedType && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastCompositionQuestions[selectedType].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {category === "Comparative" && (
        <div className="select-container">
          <label>
            Choose a type of comparative question:
            <select
              className="common-select"
              value={selectedComparativeType}
              onChange={(e) => {
                setSelectedComparativeType(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a type --</option>
              {Object.keys(PastComparativeQuestions).map((typeName) => (
                <option key={typeName} value={typeName}>
                  {typeName}
                </option>
              ))}
            </select>
          </label>

          {selectedComparativeType && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastComparativeQuestions[selectedComparativeType].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {category === "Aiste" && (
        <div className="select-container">
          <label>
            Roghnaigh ceist:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                if (selectedQuestion !== "") {
                  setQuestion(selectedQuestion);
                }
              }}
            >
              <option value="">-- Roghnaigh ceist --</option>
              {Object.keys(PastAisteQuestions)
                .sort((a, b) => b - a)
                .map((year) => (
                  <optgroup key={year} label={`Bliain: ${year}`}>
                    {PastAisteQuestions[year].map((q, i) => (
                      <option key={i} value={q}>{`[${year}] ${q.substring(0, 50)}...`}</option>
                    ))}
                  </optgroup>
                ))}
            </select>
          </label>
        </div>
      )}

      {category.startsWith('Filiocht_') && (
        <div className="select-container">
          <label>
            Roghnaigh ceist:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                setQuestion(selectedQuestion);
              }}
            >
              <option value="">-- Roghnaigh ceist --</option>
              {(() => {
                const part = category.split('_')[1];
                if (PastFiliochtQuestions[selectedPoem] && 
                    PastFiliochtQuestions[selectedPoem][part]) {
                  return Object.entries(PastFiliochtQuestions[selectedPoem][part])
                    .map(([year, q]) => (
                      <option key={year} value={q}>
                        {year}: {q}
                      </option>
                    ));
                }
                return null;
              })()}
            </select>
          </label>
        </div>
      )}

      {['FR_Email', 'FR_Opinion', 'FR_Diary', 'FR_Recit'].includes(category) && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                setQuestion(selectedQuestion);
              }}
            >
              <option value="">-- Select a question --</option>
              {Object.keys(PastFrenchQuestions[category] || {})
                .sort((a, b) => b - a)
                .map((year) => (
                  <optgroup key={year} label={`Year: ${year}`}>
                    {PastFrenchQuestions[category][year].map((q, i) => (
                      <option key={i} value={q}>{`[${year}] ${q.substring(0, 50)}...`}</option>
                    ))}
                  </optgroup>
                ))}
            </select>
          </label>
        </div>
      )}

      {['ES_Opinion', 'ES_LetterEmail', 'ES_Diary', 'ES_Note'].includes(category) && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
              }}
            >
              <option value="">-- Select a question --</option>
              {Object.keys(PastSpanishQuestions[category] || {})
                .sort((a, b) => b - a)
                .map((year) => (
                  <optgroup key={year} label={`Year: ${year}`}>
                    {PastSpanishQuestions[category][year].map((q, i) => (
                      <option key={i} value={q}>{`[${year}] ${q.substring(0, 50)}...`}</option>
                    ))}
                  </optgroup>
                ))}
            </select>
          </label>
        </div>
      )}

      {['DE_Ausserung', 'DE_Letter', 'DE_TopicalQuestions'].includes(category) && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            >
              <option value="">-- Select a question --</option>
              {Object.keys(PastGermanQuestions[category] || {})
                .sort((a, b) => b - a)
                .map((year) => (
                  <optgroup key={year} label={`Year: ${year}`}>
                    {PastGermanQuestions[category][year].map((q, i) => (
                      <option key={i} value={q}>{`[${year}] ${q.substring(0, 50)}...`}</option>
                    ))}
                  </optgroup>
                ))}
            </select>
          </label>
        </div>
      )}

      {/* Nested dropdown for oral categories */}
      {['FR_Oral', 'ES_Oral', 'DE_Oral', 'IE_Oral'].includes(category) && (
        <div className="select-container">
          {/* First, select the subcategory */}
          <label>
            {category === 'IE_Oral' ? 'Roghnaigh ábhar:' : 'Choose a topic:'}
            <select
              className="common-select"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setQuestion(""); // Reset question when subcategory changes
              }}
            >
              <option value="">{category === 'IE_Oral' ? '-- Roghnaigh ábhar --' : '-- Select a topic --'}</option>
              {Object.keys(
                category === 'FR_Oral' ? PastFrenchQuestions[category] :
                category === 'ES_Oral' ? PastSpanishQuestions[category] :
                category === 'DE_Oral' ? PastGermanQuestions[category] :
                PastIrishOralQuestions || {}
              ).map((subcategory) => (
                <option key={subcategory} value={subcategory}>
                  {subcategory}
                </option>
              ))}
            </select>
          </label>

          {/* If subcategory is selected, show the questions dropdown */}
          {selectedType && (
            <div className="select-container">
              <label>
                {category === 'IE_Oral' ? 'Roghnaigh ceist:' : 'Choose a question:'}
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                >
                  <option value="">{category === 'IE_Oral' ? '-- Roghnaigh ceist --' : '-- Select a question --'}</option>
                  {(() => {
                    // Get the appropriate questions for the selected subcategory
                    const questions = 
                      category === 'FR_Oral' ? PastFrenchQuestions[category][selectedType] :
                      category === 'ES_Oral' ? PastSpanishQuestions[category][selectedType] :
                      category === 'DE_Oral' ? PastGermanQuestions[category][selectedType] :
                      PastIrishOralQuestions[selectedType] || [];
                    
                    return questions.map((q, i) => (
                      <option key={i} value={q}>{q}</option>
                    ));
                  })()}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {/* GCSE French Q1/Q2 Questions */}
      {(category === 'GCSE_French_Q1' || category === 'GCSE_French_Q2') && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                setQuestion(selectedQuestion);
              }}
            >
              <option value="">-- Select a question --</option>
              {(() => {
                const questionKey = category === 'GCSE_French_Q1' ? 'GCSE_French_Question1' : 'GCSE_French_Question2';
                return Object.keys(PastGCSEFrenchQuestions[questionKey] || {})
                  .sort((a, b) => b - a) // Sort years descending
                  .map((year) => (
                    <optgroup key={year} label={`Year: ${year}`}>
                      {PastGCSEFrenchQuestions[questionKey][year].map((q, i) => (
                        <option key={i} value={q}>{`[${year}] ${q.substring(0, 80)}...`}</option>
                      ))}
                    </optgroup>
                  ));
              })()}
            </select>
          </label>
        </div>
      )}

      {/* GCSE Spanish Q1/Q2 Questions */}
      {(category === 'GCSE_Spanish_Q1' || category === 'GCSE_Spanish_Q2') && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                setQuestion(selectedQuestion);
              }}
            >
              <option value="">-- Select a question --</option>
              {(() => {
                const questionKey = category === 'GCSE_Spanish_Q1' ? 'Spanish_GCSE_Question1' : 'Spanish_GCSE_Question2';
                return Object.keys(PastGCSESpanishQuestions[questionKey] || {})
                  .sort((a, b) => b - a) // Sort years descending
                  .map((year) => (
                    <optgroup key={year} label={`Year: ${year}`}>
                      {PastGCSESpanishQuestions[questionKey][year].map((q, i) => (
                        <option key={i} value={q}>{`[${year}] ${q.substring(0, 80)}...`}</option>
                      ))}
                    </optgroup>
                  ));
              })()}
            </select>
          </label>
        </div>
      )}

      {/* GCSE German Q1/Q2 Questions */}
      {(category === 'GCSE_German_Q1' || category === 'GCSE_German_Q2') && (
        <div className="select-container">
          <label>
            Choose a question:
            <select
              className="common-select"
              value={question}
              onChange={(e) => {
                const selectedQuestion = e.target.value;
                setQuestion(selectedQuestion);
              }}
            >
              <option value="">-- Select a question --</option>
              {(() => {
                const questionKey = category === 'GCSE_German_Q1' ? 'GCSE_German_Question1' : 'GCSE_German_Question2';
                return Object.keys(PastGCSEGermanQuestions[questionKey] || {})
                  .sort((a, b) => b - a) // Sort years descending
                  .map((year) => (
                    <optgroup key={year} label={`Year: ${year}`}>
                      {PastGCSEGermanQuestions[questionKey][year].map((q, i) => (
                        <option key={i} value={q}>{`[${year}] ${q.substring(0, 80)}...`}</option>
                      ))}
                    </optgroup>
                  ));
              })()}
            </select>
          </label>
        </div>
      )}

      {/* Add GCSE Dickens question selection UI */}
      {category === "GCSE_19th" && (
        <div className="select-container">
          <label>
            Choose a book:
            <select
              className="common-select"
              value={selectedPlay}
              onChange={(e) => {
                setSelectedPlay(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a book --</option>
              {Object.keys(PastGCSE19thQuestions).map((bookName) => (
                <option key={bookName} value={bookName}>
                  {bookName}
                </option>
              ))}
            </select>
          </label>

          {selectedPlay && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastGCSE19thQuestions[selectedPlay].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Add GCSE Inspector question selection UI */}
      {category === "GCSE_Inspector" && (
        <div className="select-container">
          <label>
            Choose a play:
            <select
              className="common-select"
              value={selectedPlay}
              onChange={(e) => {
                setSelectedPlay(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a play --</option>
              {Object.keys(PastGCSEInspectorQuestions).map((bookName) => (
                <option key={bookName} value={bookName}>
                  {bookName}
                </option>
              ))}
            </select>
          </label>

          {selectedPlay && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastGCSEInspectorQuestions[selectedPlay].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Add GCSE Poetry question selection UI */}
      {category === "GCSE_Poetry" && (
        <div className="select-container">
          <label>
            Choose a poetry collection:
            <select
              className="common-select"
              value={selectedPlay}
              onChange={(e) => {
                setSelectedPlay(e.target.value);
                setQuestion("");
              }}
            >
              <option value="">-- Select a collection --</option>
              {Object.keys(PastGCSEPoetryQuestions).map((collectionName) => (
                <option key={collectionName} value={collectionName}>
                  {collectionName}
                </option>
              ))}
            </select>
          </label>

          {selectedPlay && (
            <div className="select-container">
              <label>
                Choose a question:
                <select
                  className="common-select"
                  value={question}
                  onChange={(e) => {
                    const selectedQuestion = e.target.value;
                    if (selectedQuestion !== "") {
                      setQuestion(selectedQuestion);
                    }
                  }}
                >
                  <option value="">-- Select a question --</option>
                  {PastGCSEPoetryQuestions[selectedPlay].map((q, index) => (
                    <option key={index} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Question input textarea */}
      <div className="question-select-container">
        <textarea
          ref={textareaRef}
          className="question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Enter your question here..."
          spellCheck="false"
        />
      </div>

      {/* Navigation buttons */}
      <div className="button-container">
        <button onClick={handleQuestionSubmit} className="submit-button">
          Next
        </button>
        <button onClick={handlePrevStep} className="secondary-button">
          Back
        </button>
      </div>
    </div>
  );
}

export default QuestionSelect; 